import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Table, Button, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt, faDownload, faSync } from '@fortawesome/free-solid-svg-icons';

interface LogFile {
  name: string;
  path: string;
  size: string;
  created: string;
  modified: string;
}

const ServerLogsView: React.FC = () => {
  const [logs, setLogs] = useState<LogFile[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
  
  useEffect(() => {
    fetchLogs();
  }, []);
  
  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${apiBaseUrl}/logs`, {
        withCredentials: true
      });
      
      setLogs(response.data.logs);
    } catch (err: any) {
      console.error('Error fetching logs:', err);
      setError(err.response?.data?.message || 'Failed to fetch log files');
    } finally {
      setLoading(false);
    }
  };
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };
  
  const handleViewLog = (path: string) => {
    window.open(apiBaseUrl + path, '_blank');
  };
  
  const handleDownloadLog = async (filename: string) => {
    try {
      const response = await axios.get(`${apiBaseUrl}/logs/view/${filename}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading log:', err);
      setError('Failed to download log file');
    }
  };
  
  return (
    <div className="mt-4">
      <div className="responsive-toolbar d-flex justify-content-between align-items-center mb-4">
        <h2>Server Logs</h2>
        <Button 
          variant="outline-primary" 
          onClick={fetchLogs}
          disabled={loading}
        >
          <FontAwesomeIcon icon={faSync} className="me-2" />
          Refresh
        </Button>
      </div>
      
      {error && (
        <Alert variant="danger">
          {error}
        </Alert>
      )}
      
      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : logs.length === 0 ? (
        <Alert variant="info">
          No log files found.
        </Alert>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Log File</th>
              <th>Size</th>
              <th>Modified</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr key={log.name}>
                <td>{log.name}</td>
                <td>{log.size}</td>
                <td>{formatDate(log.modified)}</td>
                <td>
                  <Button
                    variant="primary"
                    size="sm"
                    className="me-2"
                    onClick={() => handleViewLog(log.path)}
                  >
                    <FontAwesomeIcon icon={faFileAlt} className="me-1" /> View
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleDownloadLog(log.name)}
                  >
                    <FontAwesomeIcon icon={faDownload} className="me-1" /> Download
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
};

export default ServerLogsView;